import { Stack, Typography } from '@mui/material';
import { Key } from 'react';
import { FieldValues, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/components/ui/alert/alert';
import {
  TFormSelectOption,
  TFormSelectProps,
} from '@/shared/components/ui/form/components/form-select/form-select.types';
import { TValue } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { StyledSelect } from '@/shared/components/ui/styled-select/styled-select';
import { Tags } from '@/shared/components/ui/tags/tags';

export const FormSelect = <TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  options,
  rules,
  defaultValue,
  shouldUnregister,
  required,
  onChange,
  onSelectChange,
  customRenderValue,
  customMenuItem,
  hint,
  multiple,
  disabled,
  ...rest
}: TFormSelectProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TFieldValues>();
  const {
    field: { onChange: onFieldChange, value, ref, ...fieldProps },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const renderMenuItem = (option: TFormSelectOption<TFieldValues>) => {
    const isSelected = value === option.value;

    if (!customMenuItem) {
      return (
        <MenuItem
          key={option.value}
          value={option.value}
          selected={isSelected}
          additionalInfo={option.additionalInfo}
        >
          {option.label}
        </MenuItem>
      );
    }

    return customMenuItem(option.value as Key, option, isSelected);
  };

  const renderValue = (option?: TFormSelectOption<TFieldValues>) => {
    if (!customRenderValue) {
      return option?.label;
    }

    return customRenderValue(option);
  };

  const getValue = () => {
    if (value) {
      return value;
    }

    return multiple ? [] : '';
  };

  const renderTags = (selected: TValue<TFieldValues>[]) => {
    const names: string[] = [];

    options.forEach((option) => {
      if (selected.includes(option.value)) {
        names.push(option.label);
      }
    });

    return (
      <Tags
        tags={names.map((name) => {
          return {
            name,
          };
        })}
        visibleTags={2}
      />
    );
  };

  return (
    <Stack gap="0.75rem" width="100%">
      <StyledSelect
        ref={ref}
        label={label}
        required={required}
        value={getValue()}
        onChange={(event, child) => {
          onFieldChange(event);
          onChange?.(event, child);

          if (onSelectChange) {
            onSelectChange(event?.target?.value);
          }
        }}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography
                variant="p8"
                color={disabled ? 'textPrimary' : 'textSecondary'}
              >
                {t('ui:form.select_field', {
                  field: label,
                })}
              </Typography>
            );
          }

          if (multiple) {
            return renderTags(selected as TValue<TFieldValues>[]);
          }

          return renderValue(
            options.find((option) => option.value === selected),
          );
        }}
        error={!!error}
        multiple={multiple}
        disabled={disabled}
        {...fieldProps}
        {...rest}
      >
        {options.map((option) => renderMenuItem(option))}
      </StyledSelect>
      {hint && <Alert color="blue" text={hint} />}
    </Stack>
  );
};
